import React, { useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import userStore from '../../../../../stores/user.store';
import serverStore from '../../../../../stores/server.store';
import styled from 'styled-components';
import uptimeStore from '../../../../../stores/uptime.store';
import mongoDbStore from '../../../../../stores/mongodb.store';

const Styles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 10px;
`;

const UsageItemStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const UsageHeaderStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .title {
    font-size: 13px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
  }
  
  .value {
    font-family: monospace;
    font-size: 12px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.62);
  }
`;

const ProgressBarStyled = styled.div`
  height: 6px;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFillStyled = styled.div`
  height: 100%;
  border-radius: 3px;
  width: ${props => props.percent}%;
  background: ${props => props.accent};
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const UsageWidget = () => {
    return useObserver(() => (
        <UsageWidgetContent
            user={ userStore.user }
            serversObj={ serverStore.serversListObj }
            uptimeObj={ uptimeStore.uptimeListObj }
            mongoObj={ mongoDbStore.mongoDbListObj }
        />
    ));
};

export default UsageWidget;

const UsageWidgetContent = ({ user, serversObj, uptimeObj, mongoObj }) => {
    const totalServers = useMemo(() => {
        if (serversObj) {
            return Object.keys(serversObj).length;
        }
        return 0;
    }, [serversObj]);

    const totalServersLimit = useMemo(() => {
        if (user && user.subscription && user.subscription.servers) {
            return user.subscription.servers;
        }
        return 0;
    }, [user]);

    const totalUptime = useMemo(() => {
        if (uptimeObj) {
            return Object.keys(uptimeObj).length;
        }
        return 0;
    }, [uptimeObj]);

    const totalUptimeLimit = useMemo(() => {
        if (user && user.subscription && user.subscription.uptime) {
            return user.subscription.uptime;
        }
        return 0;
    }, [user]);

    const totalMongo = useMemo(() => {
        if (mongoObj) {
            return Object.keys(mongoObj).length;
        }
        return 0;
    }, [mongoObj]);

    const totalMongoLimit = useMemo(() => {
        if (user && user.subscription && user.subscription.mongo) {
            return user.subscription.mongo;
        }
        return 0;
    }, [user]);

    const getPercent = (current, limit) => {
        if (!limit || limit === 0) return 0;
        return Math.min(100, Math.round((current / limit) * 100));
    };

    return (
        <Styles>
            <UsageItemStyled>
                <UsageHeaderStyled>
                    <span className="title">Servers</span>
                    <span className="value">{ `${ totalServers } / ${ totalServersLimit }` }</span>
                </UsageHeaderStyled>
                <ProgressBarStyled>
                    <ProgressBarFillStyled
                        percent={ getPercent(totalServers, totalServersLimit) }
                        accent="#36A2EF"
                    />
                </ProgressBarStyled>
            </UsageItemStyled>

            <UsageItemStyled>
                <UsageHeaderStyled>
                    <span className="title">Uptime Checks</span>
                    <span className="value">{ `${ totalUptime } / ${ totalUptimeLimit }` }</span>
                </UsageHeaderStyled>
                <ProgressBarStyled>
                    <ProgressBarFillStyled
                        percent={ getPercent(totalUptime, totalUptimeLimit) }
                        accent="#66d9ef"
                    />
                </ProgressBarStyled>
            </UsageItemStyled>

            <UsageItemStyled>
                <UsageHeaderStyled>
                    <span className="title">MongoDB Monitors</span>
                    <span className="value">{ `${ totalMongo } / ${ totalMongoLimit }` }</span>
                </UsageHeaderStyled>
                <ProgressBarStyled>
                    <ProgressBarFillStyled
                        percent={ getPercent(totalMongo, totalMongoLimit) }
                        accent="#50bc5e"
                    />
                </ProgressBarStyled>
            </UsageItemStyled>
        </Styles>
    );
};